// ProgramCatalogCard.jsx
import { Card, CardContent, CardHeader } from '@/app/common/components/Card'
import { Input } from '@/app/common/components/input/Input'
import Label from '@/app/common/components/Label'
import MultiQueryPageLoader from '@/app/common/components/MultiQueryPageLoader'
import { useGetProgramCatalogCategories } from '@/queries/program_catalog/hooks/useGetProgramCatalogCategories'
import useGetSystemCatalogCategories from '@/queries/system_catalog/hooks/useGetSystemCatalogCategories'
import useGetSystemCatalogItems from '@/queries/system_catalog/hooks/useGetSystemCatalogItems'
import { RadioGroup, RadioGroupItem } from '@/shad/ui/radio-group'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shad/ui/tabs'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { ChangeEvent, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  SYSTEM_CATALOG_ITEM_STATUSES,
  SystemCatalogItemStatuses,
} from '../../system_catalog/constants/system-catalog.constants'
import ProgramCatalogContent from '../program_catalog/ProgramCatalogTab'
import SystemsCatalogAccordion from '../system_catalog/SystemCatalogTab'

const TAB_VARIANTS = ['System Catalog', 'Program Catalog'] as const

function ProgramCatalogCard() {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] =
    useState<SystemCatalogItemStatuses>('All')
  const itemCategoriesQuery = useGetSystemCatalogCategories()
  const itemsQuery = useGetSystemCatalogItems()
  const programId = useSelector(selectSelectedProgram).id

  const programCatalogQuery = useGetProgramCatalogCategories(programId)

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
  }

  return (
    <MultiQueryPageLoader
      queries={
        [
          itemCategoriesQuery.query,
          itemsQuery.query,
          programCatalogQuery.query,
        ] as const
      }
    >
      {([systemCategories, items, programCategories]) => {
        return (
          <Card>
            <CardHeader>Program Catalog Setup</CardHeader>
            <CardContent>
              <Tabs defaultValue={TAB_VARIANTS[0]}>
                <TabsList className="mb-4">
                  <TabsTrigger value={TAB_VARIANTS[0]}>
                    System Catalog
                  </TabsTrigger>
                  <TabsTrigger value={TAB_VARIANTS[1]}>
                    Program Catalog
                  </TabsTrigger>
                </TabsList>
                <Input
                  placeholder="Search"
                  value={search}
                  onChange={handleSearchChange}
                />
                <RadioGroup
                  value={statusFilter}
                  onValueChange={(value) =>
                    setStatusFilter(value as SystemCatalogItemStatuses)
                  }
                  className="flex items-center gap-8 mt-2"
                >
                  {SYSTEM_CATALOG_ITEM_STATUSES.map((option) => (
                    <div key={option} className="flex items-center gap-1">
                      <RadioGroupItem
                        value={option}
                        id={`filter-${option.toLowerCase()}`}
                      />
                      <Label htmlFor={`filter-${option.toLowerCase()}`}>
                        {option}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
                <TabsContent value={TAB_VARIANTS[0]}>
                  <SystemsCatalogAccordion
                    categories={systemCategories}
                    programCategories={programCategories}
                    onAddCategorySuccess={() => {
                      itemCategoriesQuery.invalidateQuery()
                      programCatalogQuery.invalidateQuery()
                    }}
                    items={items}
                    searchTerm={search}
                    statusFilter={statusFilter}
                  />
                </TabsContent>
                <TabsContent value={TAB_VARIANTS[1]}>
                  <ProgramCatalogContent
                    onRemoveItemSuccess={() => {
                      itemCategoriesQuery.invalidateQuery()
                      programCatalogQuery.invalidateQuery()
                    }}
                    programCategories={programCategories}
                    searchTerm={search}
                    statusFilter={statusFilter}
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        )
      }}
    </MultiQueryPageLoader>
  )
}

export default ProgramCatalogCard
